// Have to import the file directly rather than just the module, otherwise we get a 'require is undefined' error.
import videojs, { VideoJSPlayer } from 'video.js';
import { bowser } from '../../../../../global/browser-support';

import {
  PopOutButton,
  videoJsOptions,
  CloseFullscreenButton,
  PopOutButtonClicked,
  CloseFullscreenButtonClicked,
} from './video-player.constants';
import noop from 'lodash/noop';
import classNames from 'classnames';
import 'video.js/dist/video-js.css';
import styles from './video-player.module.scss';
import { createCustomButton, getTypeFromSrcUrl } from './video-player.helpers';

export type VideoPlayerProps = {
  aspectRatio: number;
  isAudio: boolean;
  media: {
    url: string;
  };
  onEnded?: () => void;
  onPlay?: (pauseMedia: VideoJSPlayer['pause']) => void;
  onPause?: () => void;
  autoplay?: boolean;
  onPopOutClick?: () => void;
  onCloseClick?: () => void;
  onPlayerReady?: (player: VideoJSPlayer) => void;
};

export type VideoPlayerState = {
  isFocused: boolean;
  ended: boolean;
};

class VideoPlayer extends React.Component<VideoPlayerProps, VideoPlayerState> {
  static defaultProps = {
    aspectRatio: 9 / 16,
    onCloseClick: noop,
    onPopOutClick: noop,
  };

  static displayName = 'VideoPlayer';

  media = React.createRef<VideoJSPlayer>();
  mediaWrapper = React.createRef<HTMLDivElement>();
  container = React.createRef<HTMLDivElement>();
  player: VideoJSPlayer;
  closeFullscreenButtonComp?: HTMLElement;
  popOutButtonComp?: HTMLElement;

  constructor(props: VideoPlayerProps) {
    super(props);

    this.state = {
      isFocused: false,
      ended: false,
    };
  }

  componentDidMount() {
    if (this.media.current) {
      this.player = videojs(
        this.media.current,
        videoJsOptions(),
        this.onPlayerReady,
      );
      if (this.props.onPlayerReady) {
        this.props.onPlayerReady(this.player);
      }

      // IE11 allows the fullscreen escape event to continue moving through DOM
      // this will cause an issue if this player is in a modal
      document.addEventListener('keydown', this.onKeyDown, true);

      // Safari browser shows strange behaviour where keyboard navigation by tab or shift + tab gets stuck on certain video controls
      // these have visibility: hidden or display: none so should not be in the document flow at all
      // but tabbings sticks unless they have tabindex="-1"
      // and in some cases their children must also have display: none
      if (bowser.safari) {
        // on first play, make the pop out button navigable
        const putControlsIntoFlow = this.putControlsIntoFlow;

        this.player.one('play', () => {
          if (this.popOutButtonComp) {
            this.popOutButtonComp.setAttribute('tabindex', '0');
          }

          // and make the big play button non-navigable
          const parentElm = this.container.current;

          if (parentElm) {
            const bigPlayButton = parentElm.querySelector(
              '.vjs-big-play-button',
            );
            if (bigPlayButton) {
              bigPlayButton.setAttribute('tabindex', '-1');
            }

            putControlsIntoFlow(parentElm);
          }
        });

        this.player.on('controlsenabled', () => {
          // restore tabindexes set by controlsdisabled
          const parentElm = this.container.current;

          if (parentElm) {
            const bigPlayButton = parentElm.querySelector(
              '.vjs-error .vjs-big-play-button',
            );
            if (bigPlayButton) {
              bigPlayButton.removeAttribute('tabindex');
            }

            putControlsIntoFlow(parentElm);
          }
        });

        const takeControlsOutOfFlow = this.takeControlsOutOfFlow;

        // ensure hidden controls do not block tabbing
        this.player.on('controlsdisabled', () => {
          const parentElm = this.container.current;

          if (parentElm) {
            const bigPlayButton = parentElm.querySelector(
              '.vjs-error .vjs-big-play-button',
            );
            if (bigPlayButton) {
              bigPlayButton.setAttribute('tabindex', '-1');
            }

            takeControlsOutOfFlow(parentElm);
          }
        });
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown, true);
  }

  componentDidUpdate(prevProps: VideoPlayerProps) {
    if (prevProps.media !== this.props.media) {
      this.player.src({
        type: getTypeFromSrcUrl(this.props.media.url),
        src: this.props.media.url,
      });
      if (this.props.isAudio) {
        this.player.pause();
        this.player.currentTime(0);
        this.player.trigger('loadstart');
      }
    }
  }

  onKeyDown = (evt: KeyboardEvent) => {
    if (
      this.player.isFullscreen() &&
      (evt.key === 'Escape' || evt.key === 'Esc')
    ) {
      evt.stopPropagation();
    }
  };

  onPlayerReady = () => {
    const {
      onEnded = noop,
      onPlay = noop,
      onPause = noop,
      autoplay = false,
    } = this.props;

    this.player.currentTime(0);
    this.player.on('ended', () => {
      onEnded();
      this.setState({
        ended: true,
      });
    });
    this.player.on('play', () => {
      this.setState({
        ended: false,
      });
      onPlay(this.pauseVideo);
    });
    this.player.on('pause', () => {
      onPause();
    });
    this.player.trigger('loadstart');

    if (autoplay) {
      this.player.play();
    } else {
      this.player.pause();
    }

    this.addCustomButtons();

    if (bowser.safari) {
      // work around Safari tabbing issues
      if (this.player.paused() && this.popOutButtonComp) {
        this.popOutButtonComp.setAttribute('tabindex', '-1');

        const parentElm = this.container && this.container.current;

        if (parentElm) {
          this.takeControlsOutOfFlow(parentElm);
        }
      }

      const closeFullscreenButtonComp = this.closeFullscreenButtonComp;

      this.player.on('fullscreenchange', function (this: VideoJSPlayer) {
        if (closeFullscreenButtonComp) {
          if (this.isFullscreen()) {
            closeFullscreenButtonComp.setAttribute('tabindex', '0');
          } else {
            closeFullscreenButtonComp.setAttribute('tabindex', '-1');
          }
        }
      });
    }
  };

  // used in Safari only
  takeControlsOutOfFlow = (parentElm: HTMLElement) => {
    const controlBar = parentElm.querySelector('.vjs-control-bar');

    if (controlBar) {
      controlBar.setAttribute('tabindex', '-1');

      controlBar.querySelectorAll('div').forEach(child => {
        if (child.getAttribute('tabindex') === '0') {
          child.setAttribute('tabindex', '-13');
        }
      });
    }

    const buttons = parentElm.querySelectorAll('button.vjs-control');

    buttons.forEach(button => {
      button.setAttribute('tabindex', '-1');
    });
  };

  // used in Safari only
  putControlsIntoFlow = (parentElm: HTMLElement) => {
    const controlBar = parentElm.querySelector('.vjs-control-bar');

    if (controlBar) {
      controlBar.setAttribute('tabindex', '-1');

      controlBar.querySelectorAll('div').forEach(child => {
        if (child.getAttribute('tabindex') === '-13') {
          child.setAttribute('tabindex', '0');
        }
      });
    }

    const buttons = parentElm.querySelectorAll('button.vjs-control');

    buttons.forEach(button => {
      button.removeAttribute('tabindex');
    });

    if (!this.player.isFullscreen() && this.closeFullscreenButtonComp) {
      this.closeFullscreenButtonComp.setAttribute('tabindex', '-1');
    } else if (this.closeFullscreenButtonComp) {
      this.closeFullscreenButtonComp.setAttribute('tabindex', '0');
    }
  };

  addCustomButtons = () => {
    createCustomButton(
      PopOutButton,
      [
        styles['custom-button'],
        styles['custom-button--top-left'],
        styles['custom-button--pop-out'],
        styles['custom-button--inline-only'],
      ],
      PopOutButtonClicked,
      polyglot.t('video_player.aria.pop_out_button_aria_label'),
    );

    createCustomButton(
      CloseFullscreenButton,
      [
        styles['custom-button'],
        styles['custom-button--top-right'],
        styles['custom-button--close-fullscreen'],
        styles['custom-button--fullscreen-only'],
      ],
      CloseFullscreenButtonClicked,
      polyglot.t('video_player.aria.close_full_screen_button_aria_label'),
    );

    this.popOutButtonComp = this.player.addChild(PopOutButton);
    this.closeFullscreenButtonComp = this.player.addChild(
      CloseFullscreenButton,
    );
  };

  pauseVideo = () => {
    return this.player.pause();
  };

  onContainerFocus = () => {
    this.setState({ isFocused: true });
  };

  onContainerBlur = () => {
    this.setState({ isFocused: false });
  };

  onContainerClick = () => {
    if (this.container.current) {
      this.container.current.blur();
      this.setState({ isFocused: false });
    }
  };

  render() {
    return (
      <div
        className={classNames(
          styles['video-player'],
          this.state.isFocused && styles['video-player--keyboard-focused'],
        )}
      >
        {this.state.ended && (
          <span className={styles['video-player__playback-ended']} role='alert'>
            {polyglot.t('video_player.playback_ended')}
          </span>
        )}
        <div
          ref={this.container}
          onFocus={this.onContainerFocus}
          onBlur={this.onContainerBlur}
          onClick={this.onContainerClick}
          className={styles['video-player__video']}
        >
          <div data-vjs-player ref={this.mediaWrapper}>
            <video
              ref={this.media}
              className='vjs-default-skin video-js'
              controls={true}
              preload='auto'
            >
              <source
                src={this.props.media.url.replace('webm', 'mp4')}
                type={getTypeFromSrcUrl(this.props.media.url)}
              />
            </video>
          </div>
        </div>
      </div>
    );
  }
}

export default VideoPlayer;
